import React, { useState, useEffect } from "react";
import {
  Home,
  ChartNoAxesColumnIncreasing,
  Clock,
  FileText,
  ChevronsLeft,
  ChevronsRight,
  Settings,
  LogOut,
  Menu,
  TableProperties,
  Shield,
  Activity,
  DollarSign,
  Sliders,
  Bug,
  Headphones
} from "lucide-react";
import finzSmall from '../assets/sidebar/finz-small.png';
import finzFull from '../assets/sidebar/finz-full.png';
import finzBlack from '../assets/logo.svg';
import { NavLink, useLocation } from "react-router-dom";

const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';

type NavItem = {
  icon: React.ReactNode;
  title: string;
  to: string;
  tutorialKey?: string;
  isActive?: (pathname: string) => boolean;
};

type SidebarProps = {
  onCollapseChange: (collapsed: boolean) => void;
  handleShowLogout: () => void;
  isMobile: boolean;
  notificationCount?: number;
  isAdmin?: boolean;
};

const Sidebar: React.FC<SidebarProps> = ({ onCollapseChange, isMobile, handleShowLogout, notificationCount = 0, isAdmin = false }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [hoveringLogo, setHoveringLogo] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isAdminPortal = isAdmin && location.pathname.startsWith('/admin');

  const businessDetailMatch = location.pathname.match(/^\/admin\/business\/([^/]+)/);
  const adminBusinessId = businessDetailMatch?.[1];
  const adminPath = (suffix: string, fallback: string) => (
    adminBusinessId ? `/admin/business/${adminBusinessId}${suffix}` : fallback
  );

  const navItems: NavItem[] = isAdminPortal ? [
    {
      icon: <Home size={19} />,
      title: adminBusinessId ? "Overview" : "Home",
      to: adminPath('', '/admin'),
      isActive: (pathname) => pathname === '/admin' || /^\/admin\/business\/[^/]+(?:\/(?:summary|home|cash|margin|drive|ledger|history))?\/?$/.test(pathname),
    },
    {
      icon: <Activity size={19} />,
      title: "Audit",
      to: adminPath('/audit', '/admin/audit'),
      isActive: (pathname) => pathname === '/admin/audit' || /^\/admin\/business\/[^/]+\/audit$/.test(pathname),
    },
    {
      icon: <Bug size={19} />,
      title: "Debug",
      to: adminPath('/debug', '/admin/debug'),
      isActive: (pathname) => pathname === '/admin/debug' || pathname === '/admin/logs' || /^\/admin\/business\/[^/]+\/debug$/.test(pathname),
    },
    {
      icon: <Sliders size={19} />,
      title: "Config",
      to: adminPath('/config', '/admin/config'),
      isActive: (pathname) => pathname === '/admin/config' || /^\/admin\/business\/[^/]+\/config$/.test(pathname),
    },
    {
      icon: <DollarSign size={19} />,
      title: "Billing",
      to: adminPath('/billing', '/admin/billing'),
      isActive: (pathname) => pathname === '/admin/billing' || /^\/admin\/business\/[^/]+\/billing$/.test(pathname),
    },
    {
      icon: <Headphones size={19} />,
      title: "Support",
      to: adminPath('/support', '/admin/support'),
      isActive: (pathname) => pathname === '/admin/support' || /^\/admin\/business\/[^/]+\/support$/.test(pathname),
    },
  ] : [
    { icon: <Home size={19} />, title: "Home", to: "/home", tutorialKey: "home" },
    { icon: <DollarSign size={19} />, title: "Cash", to: "/cash", tutorialKey: "dashboard" },
    { icon: <ChartNoAxesColumnIncreasing size={19} />, title: "Margin", to: "/margin", tutorialKey: "margin" },
    { icon: <FileText size={19} />, title: "Drive", to: "/drive", tutorialKey: "documents" },
    { icon: <TableProperties size={19} />, title: "Ledger", to: "/ledger", tutorialKey: "ledger" },
    { icon: <Clock size={19} />, title: "History", to: "/history" },
  ];

  if (isAdmin && !location.pathname.startsWith('/admin')) {
    navItems.unshift({
      icon: <Shield size={19} />,
      title: "Admin",
      to: "/admin",
      isActive: (pathname) => pathname.startsWith("/admin"),
    });
  }

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    onCollapseChange?.(next);
    setHoveringLogo(false);
  };

  useEffect(() => {
    const handleToggle = () => {
      const width = window.innerWidth;
      if (width < 850) {
        setCollapsed(true);
        onCollapseChange(true);
      }
    };
    handleToggle();
    window.addEventListener("resize", handleToggle);
    return () => window.removeEventListener("resize", handleToggle);
  }, [onCollapseChange]);

  if (isMobile) {
    const activeMobileTutorialKey = navItems.find(({ to, isActive: isItemActive }) =>
      isItemActive ? isItemActive(location.pathname) : location.pathname === to
    )?.tutorialKey;

    return (
      <div className="h-[50px] fixed top-0 left-0 w-full px-4 py-3 border-b flex justify-between items-center bg-white z-[2000]">
        <NavLink to="/home">
          <img src={finzBlack} alt="logo" className="h-6" />
        </NavLink>
        <button
          data-tutorial-nav={activeMobileTutorialKey}
          onClick={() => setIsOpen(!isOpen)}
          className="text-gray-800"
        >
          <Menu size={22} />
        </button>
        {isOpen && (
          <div className="absolute top-14 right-4 w-56 bg-white shadow-lg rounded-lg border">
            <nav className="flex flex-col divide-y text-sm">
              {navItems.map(({ icon, title, to, tutorialKey, isActive: isItemActive }) => {
                const isActive = isItemActive ? isItemActive(location.pathname) : location.pathname === to;
                return (
                  <NavLink
                    key={title}
                    to={to}
                    end={!isItemActive}
                    className={`${isActive ? "bg-gray-100" : "bg-white"} flex items-center gap-3 px-4 py-3 hover:bg-gray-100`}
                    onClick={() => setIsOpen(false)}
                  >
                    <div className={isActive ? "text-indigo-400" : "text-gray-800"}>{icon}</div>
                    <div className={isActive ? "text-indigo-400" : "text-gray-800"}>{title}</div>
                  </NavLink>
                );
              })}
              {!isAdminPortal && (
                <NavLink
                  to="/settings"
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) => `flex items-center justify-between px-4 py-3 ${isActive ? "bg-gray-100" : "bg-white"}`}
                >
                  <div className="flex items-center gap-3">
                    <Settings size={18} className="text-gray-800" />
                    <span>Settings</span>
                  </div>
                </NavLink>
              )}
              <button onClick={handleShowLogout} className="flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-100">
                <LogOut size={18} className="text-gray-800" />
                <span>Logout</span>
              </button>
            </nav>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`fixed top-0 left-0 bg-gray-800 text-white transition-all duration-300 z-[100] flex flex-col h-[100dvh] 
      ${collapsed ? 'w-[65px]' : 'w-[200px]'} overflow-visible`}
    >
      <div className="flex flex-col flex-1">
        {/* Header Section */}
        <div className="px-5 flex items-center justify-between pt-[18px] h-12 relative">
          <div className="relative flex items-center w-full">
            <NavLink to="/home">
              {!collapsed && <img src={finzFull} alt="logo" className="h-auto" />}
            </NavLink>
            <div
              className="absolute left-0 w-6"
              onMouseEnter={() => setHoveringLogo(true)}
              onMouseLeave={() => setHoveringLogo(false)}
            >
              <img src={finzSmall} alt="logo" className={collapsed && !hoveringLogo ? "block" : "hidden"} />
              <button onClick={toggleCollapse} className={collapsed && hoveringLogo ? "block" : "hidden"}>
                <ChevronsRight className="text-white hover:text-indigo-300" size={20} />
              </button>
            </div>
          </div>
          {!collapsed && (
            <button onClick={toggleCollapse}>
              <ChevronsLeft className="text-white" size={20} />
            </button>
          )}
        </div>

        {/* Navigation Section */}
        <nav className="flex flex-col mt-16">
          {navItems.map(({ icon, title, to, tutorialKey, isActive: isItemActive }) => {
            const isActive = isItemActive ? isItemActive(location.pathname) : location.pathname === to;
            return (
              <NavLink
                key={to}
                to={to}
                end={!isItemActive}
                className={() =>
                  `relative group flex items-center gap-3 border-0 py-2 font-medium transition-all duration-200 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0
                ${isActive ? "bg-gray-700" : "hover:bg-gray-700"} px-5`
                }
              >
                <>
                  <div data-tutorial-nav={tutorialKey} className={`p-1 ${isActive ? "text-indigo-400" : "text-white"}`}>{icon}</div>
                  {!collapsed && <span className="text-base">{title}</span>}

                  {/* TOOLTIP */}
                  {collapsed && (
                    <span className="absolute left-[65px] opacity-0 transition-opacity rounded bg-gray-800 px-2 py-1 text-xs text-white group-hover:opacity-100 z-[110] whitespace-nowrap shadow-xl border border-gray-700 ml-2 pointer-events-none">
                      {title}
                    </span>
                  )}
                </>
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Footer Section (Settings & Logout) */}
      <div className="flex flex-col mb-5">
        {!isAdminPortal && (
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `relative group px-5 flex items-center justify-between border-0 py-2 font-medium transition-all duration-200 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0 ${isActive ? "bg-gray-700" : "hover:bg-gray-700"}`
            }
          >
            {({ isActive }) => (
              <>
                <div className="flex items-center gap-2">
                  <div className={`p-1 ${isActive ? "text-indigo-400" : "text-white"}`}>
                    <Settings size={18} />
                  </div>
                  {!collapsed && <span className="text-base">Settings</span>}
                </div>
                {notificationCount > 0 && (
                  <div className={`${collapsed ? "absolute top-2 right-4 w-2 h-2" : "px-1.5 h-4 text-[10px]"} bg-red-500 text-white font-bold flex items-center justify-center rounded-full`}>
                    {!collapsed && notificationCount}
                  </div>
                )}
                {collapsed && (
                  <span className="absolute left-[65px] opacity-0 transition-opacity rounded bg-gray-800 px-2 py-1 text-xs text-white group-hover:opacity-100 z-[110] whitespace-nowrap shadow-xl border border-gray-700 ml-2 pointer-events-none">
                    Settings
                  </span>
                )}
              </>
            )}
          </NavLink>
        )}

        <button
          onClick={handleShowLogout}
          className="relative group px-5 flex w-full items-center gap-3 border-0 py-2 text-left font-medium transition-all duration-200 outline-none ring-0 hover:bg-gray-700 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0"
        >
          <div className="text-white p-1">
            <LogOut size={18} />
          </div>
          {!collapsed && <span className="text-base">Logout</span>}
          {collapsed && (
            <span className="absolute left-[65px] opacity-0 transition-opacity rounded bg-gray-800 px-2 py-1 text-xs text-white group-hover:opacity-100 z-[110] whitespace-nowrap shadow-xl border border-gray-700 ml-2 pointer-events-none">
              Logout
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
